/**
 * Cache statistics and monitoring
 * Handles performance metrics and cache analytics
 */

import type { CacheStats } from '../types/cache-types';

export class CacheStatsManager {
  private stats = {
    hits: 0,
    misses: 0,
    evictions: 0,
    securityViolations: 0,
    totalAccesses: 0,
    totalAccessTime: 0,
  };

  /**
   * Record cache hit
   */
  recordHit(accessTime: number = 0): void {
    this.stats.hits++;
    this.stats.totalAccesses++;
    this.stats.totalAccessTime += accessTime;
  }

  /**
   * Record cache miss
   */
  recordMiss(accessTime: number = 0): void {
    this.stats.misses++;
    this.stats.totalAccesses++;
    this.stats.totalAccessTime += accessTime;
  }

  /**
   * Record cache eviction
   */
  recordEviction(count: number = 1): void {
    this.stats.evictions += count;
  }

  /**
   * Record security violation
   */
  recordSecurityViolation(): void {
    this.stats.securityViolations++;
  }

  /**
   * Get current cache statistics
   */
  getStats(totalEntries: number, memoryUsage: number): CacheStats {
    const totalRequests = this.stats.hits + this.stats.misses;

    return {
      totalEntries,
      hitRate: totalRequests > 0 ? this.stats.hits / totalRequests : 0,
      missRate: totalRequests > 0 ? this.stats.misses / totalRequests : 0,
      evictionCount: this.stats.evictions,
      securityViolations: this.stats.securityViolations,
      averageAccessTime: this.stats.totalAccesses > 0
        ? this.stats.totalAccessTime / this.stats.totalAccesses
        : 0,
      memoryUsage,
    };
  }

  /**
   * Reset statistics
   */
  resetStats(): void {
    this.stats = {
      hits: 0,
      misses: 0,
      evictions: 0,
      securityViolations: 0,
      totalAccesses: 0,
      totalAccessTime: 0,
    };
  }

  /**
   * Get performance metrics
   */
  getPerformanceMetrics(): {
    hitRate: number;
    missRate: number;
    averageAccessTime: number;
    totalAccesses: number;
    efficiency: number;
  } {
    const totalRequests = this.stats.hits + this.stats.misses;
    const hitRate = totalRequests > 0 ? this.stats.hits / totalRequests : 0;
    const missRate = totalRequests > 0 ? this.stats.misses / totalRequests : 0;
    const averageAccessTime = this.stats.totalAccesses > 0
      ? this.stats.totalAccessTime / this.stats.totalAccesses
      : 0;

    // Efficiency score based on hit rate and access time
    const efficiency = hitRate * 100 - (averageAccessTime / 10);

    return {
      hitRate,
      missRate,
      averageAccessTime,
      totalAccesses: this.stats.totalAccesses,
      efficiency: Math.max(0, Math.min(100, efficiency)),
    };
  }

  /**
   * Check if cache performance is healthy
   */
  isPerformanceHealthy(): {
    healthy: boolean;
    issues: string[];
    recommendations: string[];
  } {
    const metrics = this.getPerformanceMetrics();
    const issues: string[] = [];
    const recommendations: string[] = [];

    // Check hit rate
    if (metrics.hitRate < 0.7 && metrics.totalAccesses > 100) {
      issues.push('Low cache hit rate');
      recommendations.push('Consider increasing cache TTL or adjusting cache strategy');
    }

    // Check access time
    if (metrics.averageAccessTime > 10) {
      issues.push('High average access time');
      recommendations.push('Consider optimizing cache storage or reducing data size');
    }

    // Check security violations
    if (this.stats.securityViolations > 0) {
      issues.push('Security violations detected');
      recommendations.push('Review security configuration and monitor for threats');
    }

    return {
      healthy: issues.length === 0,
      issues,
      recommendations,
    };
  }

  /**
   * Export stats for monitoring systems
   */
  exportForMonitoring(): Record<string, number> {
    return {
      cache_hits: this.stats.hits,
      cache_misses: this.stats.misses,
      cache_evictions: this.stats.evictions,
      cache_security_violations: this.stats.securityViolations,
      cache_total_accesses: this.stats.totalAccesses,
      cache_average_access_time: this.stats.totalAccesses > 0
        ? this.stats.totalAccessTime / this.stats.totalAccesses
        : 0,
      cache_hit_rate: this.getPerformanceMetrics().hitRate,
      cache_efficiency_score: this.getPerformanceMetrics().efficiency,
    };
  }
}